<template>
  <div class="video" :class="{ 'video--full': fullSize }">
    <img v-if="preview" :src="previewThumbnail" :alt="alt">
    <video
      v-else-if="file"
      ref="videoRef"
      :src="previewThumbnail"
      playsinline
      muted
      preload="metadata"
    />

    <div v-show="!fullSize" class="video__overlay" />
    <div class="video__icon">
      <span class="icon-play" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ReelsObject } from '~/types/common'

type Props = {
  preview?: ReelsObject | null
  file?: File | null
  isCdnVideo?: boolean
  fullSize?: boolean
  alt?: string
}

const props = withDefaults(defineProps<Props>(), {
  isCdnVideo: true,
  alt: '',
  preview: null,
  file: null
})

const videoRef = useTemplateRef<HTMLVideoElement>('videoRef')

const previewThumbnail = computed(() => {
  if (props.preview?.video_thumbnail) {
    return getImagePath({
      name: props.preview.video_thumbnail,
      size: '300x300'
    })
  }
  if (props.file) {
    return URL.createObjectURL(props.file)
  }
  return ''
})

const handleSetVideoPoster = () => {
  if (videoRef.value) {
    videoRef.value.addEventListener('loadedmetadata', () => {
      videoRef.value!.currentTime = 1
    })
  }
}

onMounted(() => {
  if (props.file) {
    nextTick(handleSetVideoPoster)
  }
})
</script>

<style lang="scss" scoped>
.video {
  position: relative;
  width: 100%;
  height: 100%;

  border-radius: 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: $white-color;

  cursor: pointer;

  & > img,
  & > video {
    padding: 8px;

    width: 100%;
    height: 100%;

    object-fit: cover;

    background-color: $font-color;
  }

  &__icon {
    position: absolute;

    color: $primary-color;
    background-color: $secondary-color;
    font-size: 1.2rem;

    border-radius: 50%;
    opacity: 0.9;

    pointer-events: none;

    width: 2.4rem;
    height: 2.4rem;

    display: flex;
    align-items: center;
    justify-content: center;

    & > * {
      display: flex;
      justify-content: flex-end;

      width: 1.2rem;
      height: 1.2rem;
    }
  }

  &__overlay {
    position: absolute;

    width: calc(100% - 16px);
    height: calc(100% - 16px);

    background-color: $white-color;
    opacity: 0.25;

    transition: $transition-time opacity;
  }

  &--full {
    & > img,
    & > video {
      padding: 0;
    }

    & > .video__icon {
      width: 32px;
      height: 32px;

      font-size: 1.6rem;

      & > * {

      width: 1.6rem;
      height: 1.6rem;
    }
    }
  }

  & > video {
    padding: 0;

    & + .video__overlay {
      width: 100%;
      height: 100%;
    }
  }
  &:hover .video__overlay {
    opacity: 0;
  }
}
</style>
